import NotificationBox from "Components/CommonComponents/NotificationsBar/NotificationBox";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { List, Skeleton } from "antd";
import NotiificationNoDataIcon from "Assets/Icons/NotificationNoDataIcon";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import "./index.css";
const StyledSpan = styled<any>("span")`
  color: var(--Grey-60, #757575);
  font-size: ${({ screen }: any) =>
    ["xxl", "xl", "lg"].includes(screen) ? "16px" : "8px"};
  font-style: normal;
  font-weight: 400;
  margin-top: 16px;
`;
const StyledDivHeading = styled<any>("span")`
  border-bottom: 0.5px solid var(--Grey-30, #e2e2e2);
  padding: 12px;
`;
const StyledSpanHeading = styled<any>("span")`
  color: var(--Grey-80, #333);
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 140% */
`;
const StyledSpanViewMore = styled<any>("span")`
  color: var(--Aqua-60, #008dc4);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
`;
const StyledMessageListingDiv = styled<any>("div")`
  overflow: auto;
  max-height: 400px;
`;

interface NotificationBarIF {
  handleAfterRoute: any;
  notifications: any[];
  nextHandler: any;
  hasMore: boolean;
  loading: boolean;
  setShowNotification?: any;
  unreadPostComments?: any[];
  unreadPostCommentsLoading?: boolean;
  onUnreadPostCommentClick?: (batchId: number, subject: string) => void;
}
const NotificationBar = ({
  handleAfterRoute,
  notifications,
  hasMore,
  nextHandler,
  loading,
  setShowNotification,
  unreadPostComments = [],
  onUnreadPostCommentClick,
}: NotificationBarIF) => {
  const navigate = useNavigate();
  const { screen } = useWindowSize();
  const safeUnreadPostComments = Array.isArray(unreadPostComments)
    ? unreadPostComments
    : [];
  const hasUnreadPostComments = safeUnreadPostComments.length > 0;
  const hasNotifications = notifications.length > 0;
  const hasAnyNotifications = hasUnreadPostComments || hasNotifications;

  return (
    <div>
      <StyledDivHeading className="d-flex justify-content-between align-items-center">
        <StyledSpanHeading>Notification</StyledSpanHeading>
        <StyledSpanViewMore
          onClick={() => {
            navigate("/admin/notifications", {
              state: {
                notifications,
                unreadPostComments: safeUnreadPostComments,
              },
            });
            handleAfterRoute();
          }}
        >
          View More
        </StyledSpanViewMore>
      </StyledDivHeading>
      {hasAnyNotifications ? (
        <StyledMessageListingDiv screen={screen}>
          <div
            id="scrollableDiv"
            style={{
              height: "400px",
              overflow: "auto",
            }}
          >
            {hasUnreadPostComments &&
              safeUnreadPostComments.map((notification: any) => {
                const batchId = notification?.batchId;
                const subject = notification?.subject;
                const unreadCount = notification?.unreadCount || 0;
                return (
                  <div
                    key={`unread-post-comment-${batchId}-${subject}`}
                    className="unread-post-comment-notification"
                    onClick={() => {
                      pushToDataLayerForGTM({
                        event: "ag_notifications_click_unread_post_comment",
                        BATCH_ID: batchId,
                        SUBJECT: subject,
                        UNREAD_COMMENTS_COUNT: unreadCount,
                      });
                      onUnreadPostCommentClick?.(batchId, subject);
                    }}
                  >
                    <div className="unread-post-comment-title">
                      New activity on your post
                    </div>
                    <div className="unread-post-comment-message">
                      You have {unreadCount} new unread messages in Batch{" "}
                      {batchId} for the {subject || "-"} subject.
                    </div>
                    <div className="unread-post-comment-cta">View Batch</div>
                  </div>
                );
              })}
            {hasNotifications && (
              <InfiniteScroll
                dataLength={notifications.length}
                next={nextHandler}
                hasMore={hasMore}
                loader={
                  loading ? (
                    <Skeleton
                      avatar
                      paragraph={{
                        rows: 1,
                      }}
                      active
                    />
                  ) : null
                }
                scrollableTarget="scrollableDiv"
              >
                <List
                  dataSource={notifications}
                  renderItem={(ele: any, index: number) => (
                    <List.Item
                      key={ele.email}
                      onClick={() => {
                        const daysOld = Math.floor(
                          (Date.now() - new Date(ele.createdOn).getTime()) /
                            (1000 * 60 * 60 * 24)
                        );
                        pushToDataLayerForGTM({
                          event: "ag_notifications_click_notification",
                          NOTIFICATION_ID: ele?.id,
                          NOTIFICATION_TYPE: ele?.type,
                          DAYS_OLD: daysOld,
                        });
                        pushToDataLayerForGTM({
                          event: "ag_notifications_dismiss_notification",
                          NOTIFICATION_ID: ele?.id,
                          STATUS: "READ",
                        });
                        setShowNotification(false);
                      }}
                    >
                      <NotificationBox
                        key={ele.createdOn + index}
                        imageUrl={ele.attachmentLink}
                        primaryMessage={ele?.type}
                        message={ele?.messageBody}
                        date={ele?.createdOn}
                      ></NotificationBox>
                    </List.Item>
                  )}
                />
              </InfiniteScroll>
            )}
          </div>
        </StyledMessageListingDiv>
      ) : (
        <StyledMessageListingDiv screen={screen}>
          <div
            className="d-flex justify-content-center align-items-center flex-column h-100 p-4"
            style={{
              background: "#F5F5F5",
            }}
          >
            <div>
              <NotiificationNoDataIcon />
            </div>
            <div className="d-flex align-items-center flex-column">
              <StyledSpan screen={screen}>
                Notifications data not available
              </StyledSpan>
            </div>
          </div>
        </StyledMessageListingDiv>
      )}
    </div>
  );
};

export default NotificationBar;
